/**
 * Validates a Brazilian CPF (Cadastro de Pessoas Físicas).
 * @param cpf The CPF string. Can contain dots and dashes.
 * @returns True if the CPF is valid, false otherwise.
 */
export const isValidCpf = (cpf: string): boolean => {
    if (!cpf) return false;

    // Remove non-digit characters
    const cpfClean = cpf.replace(/[^\d]/g, '');

    // CPFs must have 11 digits and cannot be all the same digit
    if (cpfClean.length !== 11 || /^(\d)\1{10}$/.test(cpfClean)) {
        return false;
    }

    let sum = 0;
    let remainder: number;

    // Validate first check digit
    for (let i = 1; i <= 9; i++) {
        sum = sum + parseInt(cpfClean.substring(i - 1, i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) {
        remainder = 0;
    }
    if (remainder !== parseInt(cpfClean.substring(9, 10))) {
        return false;
    }

    // Validate second check digit
    sum = 0;
    for (let i = 1; i <= 10; i++) {
        sum = sum + parseInt(cpfClean.substring(i - 1, i)) * (12 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) {
        remainder = 0;
    }
    if (remainder !== parseInt(cpfClean.substring(10, 11))) {
        return false;
    }

    return true;
};

/**
 * Validates a Brazilian CNPJ (Cadastro Nacional da Pessoa Jurídica).
 * @param cnpj The CNPJ string. Can contain dots, slashes and dashes.
 * @returns True if the CNPJ is valid, false otherwise.
 */
export const isValidCnpj = (cnpj: string): boolean => {
    if (!cnpj) return false;

    // Remove non-digit characters
    const cnpjClean = cnpj.replace(/[^\d]/g, '');

    // CNPJs must have 14 digits and cannot be all the same digit
    if (cnpjClean.length !== 14 || /^(\d)\1{13}$/.test(cnpjClean)) {
        return false;
    }

    let sum = 0;
    let remainder: number;

    // Validate first check digit
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    for (let i = 0; i < 12; i++) {
        sum += parseInt(cnpjClean.charAt(i)) * weights1[i];
    }
    remainder = sum % 11;
    const firstDigit = remainder < 2 ? 0 : 11 - remainder;

    if (firstDigit !== parseInt(cnpjClean.charAt(12))) {
        return false;
    }

    // Validate second check digit
    sum = 0;
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    for (let i = 0; i < 13; i++) {
        sum += parseInt(cnpjClean.charAt(i)) * weights2[i];
    }
    remainder = sum % 11;
    const secondDigit = remainder < 2 ? 0 : 11 - remainder;

    if (secondDigit !== parseInt(cnpjClean.charAt(13))) {
        return false;
    }

    return true;
};

/**
 * Formats a CNPJ string with dots, slash and dash.
 * @param cnpj The CNPJ string (numbers only or already formatted)
 * @returns Formatted CNPJ (XX.XXX.XXX/XXXX-XX)
 */
export const formatCnpj = (cnpj: string): string => {
    if (!cnpj) return cnpj;

    // Remove all non-digit characters
    const numbers = cnpj.replace(/[^\d]/g, '');

    // Apply mask
    if (numbers.length <= 2) {
        return numbers;
    } else if (numbers.length <= 5) {
        return numbers.replace(/(\d{2})(\d{0,3})/, '$1.$2');
    } else if (numbers.length <= 8) {
        return numbers.replace(/(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3');
    } else if (numbers.length <= 12) {
        return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4');
    } else {
        return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
    }
}; 